import { filterMethod, logRef } from '../filterPreconisation';
import { PackageData } from '../package';
import { isKnownCategories } from '../products';
import { SubTheme } from '../theme';
import * as api from '../../apiParticulierService';

// Read me
//
// ACCRONYMES :
// PAC = pompe a chaleur
// AA = air/air
// AE = Air/eau
//
// WARNING :
// Dans ce fichier on trouve quelques fonctions qui permettent de filtrer les PAC selon un critère,
// exemple : keepPacPec et removePacPec.
// ATTENTION removePacPec n'est pas égal a !keepPacPec
// il en va de même pour toutes les fonctions.

//#region  les petites fonctions keep et remove, et les regex.

// quelques chaines et regex pour le filtrage et le tri des packages de pompes à chaleur.
// [0-9.,]+ prend aussi les PAC 4.5kw
// Les packages basse température ont -BT- dans leur reférence.
const PAC_BASSE_TEMP_MIDFIX = '-BT-';
// Les pompes à chaleurs qui permettent aussi la gestion de l'eau chaude, pour rempalcer les chaudière avec 'eau chaude liée au chauffage'
const PAC_PEC_MIDFIX = '-PEC-';
// regex pour trouver les PAC AE basse temperature
const PAC_AIR_EAU_BASSE_TEMPERATURE_REGEX = /^PAC-AE-([0-9.,]+)-BT-/;
// regex pour trouver les PAC AE moyenne et haute température (ou non basse temperature)
const PAC_AIR_EAU_MOYENNE_HAUTE_TEMPERATURE_REGEX = /^(?!.*-BT-)PAC-AE-([0-9.,]+)-(MT-)?/; // (?!.*-BT-) signifie sauf si il y a -BT- quelque part.
// regex pour trouver la puissance, ou nombre de plit
export const PAC_SPLIT_REGEX = /^PAC-A(A|E)-([0-9.,]+)-/;
// les pompes a chaleurs qui permettent de gérer deux réseaux d'eau (exemple, plancher chauffant + radiateur à un autre étage.)
const PAC_MULTIPLE_HYDRONIC_ZONES_MIDFIX = '-2R-'; // pour 2 Reseaux
// gestion des types de courant mono ou tri phasés.
const PAC_MONOPHASE = '-M-'; // Mono phasée
const PAC_MONOPHASE_END = '-M'; // Mono phasée
const PAC_TRIPHASE = '-T-'; // triphasée
const PAC_TRIPHASE_END = '-T'; // triphasée
export const PAC_AA_START = 'PAC-AA';
export const PAC_AE_START = 'PAC-AE';
//

const keepPacAEBasseTemp = (pack: PackageData): boolean => {
    // PLAN A : normalement cette règle fait tout. on cherche la regex qui dit PAC A/E et -BT- ...
    if (pack.reference) {
        const match = pack.reference.match(PAC_AIR_EAU_BASSE_TEMPERATURE_REGEX);
        if (match && match.length >= 1) {
            return true;
        }
    }

    // Si la regex suffit pas, CE QUI NE DEVRAIT PAS ARRIVER.
    // supprimer à partir de là à l'avenir.

    // On passe au plans B, C et D
    // plan B selon la catégorie de produit et si la réference contient "-BT-"
    if (pack.mainProduct.categorie !== 'Air/Eau' && pack.mainProduct.categorie !== 'Pac Air Eau') return false;
    if (pack.reference.includes(PAC_BASSE_TEMP_MIDFIX)) {
        console.log('isPacAEBasseTemp => ref NOT match => BUT categorie + -BT-');
        return true;
    }

    // ancienne méthode, aléatoire sur le nom de package.
    // Laissé pour la préprod
    // plan B seleon la catégorie de produit et si la réference contient "-BT-"
    if (pack.mainProduct.nom.includes('Basse Temp')) {
        console.log("isPacAEBasseTemp => ref NOT match => BUT 'Basse Temp' in title");
        return true;
    }

    return false;
};

const keepPacAEMoyenneHauteTemp = (pack: PackageData): boolean => {
    // PLAN A : normalement cette règle fait tout. on cherche la regex qui dit PAC A/E et -MT- sans -BT- ...
    if (pack.reference) {
        const match = pack.reference.match(PAC_AIR_EAU_MOYENNE_HAUTE_TEMPERATURE_REGEX);
        if (match && match.length >= 1) {
            return true;
        }
    }

    // Si la regex suffit pas, CE QUI NE DEVRAIT PAS ARRIVER.
    // supprimer à partir de là à l'avenir.

    // On tentes les autres approches :
    if (pack.mainProduct.categorie !== 'Air/Eau' && pack.mainProduct.categorie !== 'Pac Air Eau') return false;
    if (pack.reference.includes(PAC_BASSE_TEMP_MIDFIX)) return false;

    // aléatoire
    // Laissé pour la préprod
    if (pack.mainProduct.nom.includes('Basse Temp') || pack.mainProduct.nom.includes('60°C')) return false;

    console.log('isPacAEMoyenneHauteTemp => ref NOT match => BUT ok any way for ref ' + pack.reference);
    return true;
};
const removePacAEBasseTemp = (pack: PackageData): boolean => {
    // doit retourner true pour garder, false pour supprimer.
    return !keepPacAEBasseTemp(pack);
};
const removePacAEMoyenneHauteTemp = (pack: PackageData): boolean => {
    // doit retourner true pour garder, false pour supprimer.
    return !keepPacAEMoyenneHauteTemp(pack);
};
const keepPacPec = (pack: PackageData): boolean => {
    if (!pack.reference) return false;
    return pack.reference.includes(PAC_PEC_MIDFIX);
};
const removePacPec = (pack: PackageData): boolean => {
    if (!pack.reference) return false;
    return !pack.reference.includes(PAC_PEC_MIDFIX);
};
const keepPac2R = (pack: PackageData): boolean => {
    if (!pack.reference) return false;
    return pack.reference.includes(PAC_MULTIPLE_HYDRONIC_ZONES_MIDFIX);
};
const removePac2R = (pack: PackageData): boolean => {
    if (!pack.reference) return false;
    return !pack.reference.includes(PAC_MULTIPLE_HYDRONIC_ZONES_MIDFIX);
};

// pour le tri phasé, mono phasé,
// certaines PAC présentent les deux variations, dans ce cas l'indicateur -T- -T -M- ou -M est présent.
// si l'indicateur n'est pas présent, c'est du mono.
const keepPacMonophase = (pack: PackageData): boolean => {
    if (!pack.reference) return false;
    if (pack.reference.includes(PAC_MONOPHASE) || pack.reference.endsWith(PAC_MONOPHASE_END)) return true;
    return !pack.reference.includes(PAC_TRIPHASE) && !pack.reference.endsWith(PAC_TRIPHASE_END); // true si pas tri phasé.
};
const keepPacTriphase = (pack: PackageData): boolean => {
    if (!pack.reference) return false;
    return pack.reference.includes(PAC_TRIPHASE) || pack.reference.endsWith(PAC_TRIPHASE_END);
};

//#endregion

//#region les petites fonction de tri des PAC.

/**
 * Séparer les packages du theme en 2 parties : PacAA et PacAE
 * @param subTheme
 * @returns un objet composé des deux listes.
 */
const dispatchPacByType = (subTheme: SubTheme): { pacsAA: Array<PackageData>; pacsAE: Array<PackageData> } => {
    const pacAE = Array<PackageData>();
    const pacAA = Array<PackageData>();

    for (const pack of subTheme.packages) {
        if (pack.reference.startsWith(PAC_AA_START) || (isKnownCategories(pack.mainProduct.categorie) && pack.mainProduct.categorie === 'Pac Air Air')) {
            pacAA.push(pack);
        }
        if (pack.reference.startsWith(PAC_AE_START) || (isKnownCategories(pack.mainProduct.categorie) && pack.mainProduct.categorie === 'Pac Air Eau')) {
            pacAE.push(pack);
        }
    }

    return { pacsAA: pacAA, pacsAE: pacAE };
};

export const extractNbSplit = (pack: PackageData): number => {
    if (pack.reference) {
        const matchAA = pack.reference.match(PAC_SPLIT_REGEX); // = /^PAC-A(A|E)-([0-9.,]+)-/;
        if (matchAA && matchAA.length >= 2) {
            const nbsplit = matchAA[2]; // match[0] toute la chaine, match[1] premier grope de parentheèse (A|E) match[2] le nombre de split
            //console.log(pack.reference + ' => ' + nbsplit);
            return +nbsplit;
        }
    }

    // le nom du main product contient "XX Splits"
    let result = +pack.mainProduct.nom.replace(/^.*[^0-9]([0-9]+) SPLITS.*$/i, '$1');
    if (!Number.isNaN(result)) return result;

    // le nom du package contient "XX Splits"
    result = +pack.title.replace(/^.*[^0-9]([0-9]+) SPLITS.*$/i, '$1');
    if (!Number.isNaN(result)) return result;

    // le nom du main product contient "CHAPPEE (XX)"
    result = +pack.mainProduct.nom.replace(/^.*CHAPPEE ?\(([0-9]+)\).*$/i, '$1');
    if (!Number.isNaN(result)) return result;

    return 0;
};

const compareBySplit = (A: PackageData, B: PackageData): number => {
    const splitA = extractNbSplit(A);
    const splitB = extractNbSplit(B);
    return splitA - splitB;
};

//#endregion

/**
 * filtrer les packages inutiles du theme 'Chauffage'. Le theme sera modifé et retourné
 * @param audit l'audit sur lequel on préconise
 * @param subTheme le theme dont le type doit être 'Chauffage'. Le paramètre sera modifé !
 * @returns le theme filtré.
 */
export const filterChauffage: filterMethod = async (audit: any, subTheme: SubTheme): Promise<SubTheme> => {
    //logRef('filterChauffage', subTheme.packages);
    //console.log('passed in filterChauffage, input count = ' + theme.packages.length);

    // si pas de package, on laisse tomber, c'est déjà filtré !
    if (subTheme.packages.length === 0) return subTheme;

    // algo :
    // séparer les pac AA et les pac AE
    // trouver la bonne pac AE,
    // trouver la bonne pac AA,
    // les merger dans une liste et les reoturner.

    let { pacsAA, pacsAE } = dispatchPacByType(subTheme);

    // trouver LA seule pac AE
    const pacAE = await getTheOnePacAE(audit, pacsAE);
    // trouver LA seule pac AA
    const pacAA = getTheOnePacAA(audit, pacsAA);

    // merger les deux packs dans une liste.
    const packs = Array<PackageData>();

    if (pacAE) {
        packs.push(pacAE); // cette pac est selectionnée !
    }
    if (pacAA) {
        packs.push(pacAA);
        // si Convecteur / panneaux rayonnant / Ventilo convecteur, on selectionne cette pacAA.
        const heater = audit.heaterFeature.value;
        pacAA.applicable = heater === '0' || heater === '1' || heater === '2';
    }

    // log
    // if (pacAE) console.log('Final selected pacAE : ' + pacAE.reference + ' => ' + extractNbSplit(pacAE) + ' / ' + pacAE.mainProduct.puissance);
    // else console.log('Final selected pacAE : undefined');
    // if (pacAA) console.log('Final selected pacAA : ' + pacAA.reference + ' => ' + extractNbSplit(pacAA) + ' / ' + pacAA.mainProduct.puissance);
    // else console.log('Final selected pacAA : undefined');

    // utiliser la liste et la retourner
    subTheme.packages = packs;

    return subTheme;
};

/**
 * Cette fonction va choisir une seule (ou zero) PAC AE, selon les données de l'audit
 * @param audit
 * @param pacsAE
 * @returns undefined si rien ne correspond. LE package PacAE selectionné (applicable = true) adapté à la la situation
 */
const getTheOnePacAE = async (audit: any, pacsAE: Array<PackageData>): Promise<PackageData | undefined> => {
    // si audit mal remplit ou si pas de packages
    if (pacsAE.length <= 0) return undefined;

    const auditSafeData = extractOtherData(audit);
    if (!auditSafeData) return undefined;

    logRef('Départ AE', pacsAE);

    // on fait les filtrage dans un ordre précis.
    // certains filtres peuvent être réordonnés, d'autres non.
    // Il faut finir par la selection par puissance.

    // on retire les pac pas adapté au compteur.
    pacsAE = pacsAE.filter(auditSafeData.isTriphase ? keepPacTriphase : keepPacMonophase);
    logRef(auditSafeData.isTriphase ? 'Triphasé' : 'Monophasé', pacsAE);

    // on selectionne les PAC qui ont (ou pas) l'eau chaude l'iée au chauffage => -PEC-
    pacsAE = pacsAE.filter(auditSafeData.isPec ? keepPacPec : removePacPec);
    logRef(auditSafeData.isPec ? 'PEC' : '! PEC', pacsAE);

    if (auditSafeData.isMultipleHydronicZone) {
        // si on a deux zone hydronique
        // on prend les pac qui ont -2R-
        pacsAE = pacsAE.filter(keepPac2R);
        logRef('2R', pacsAE);
    } else {
        // si on a qu'une zone hydronique, filtre et on garde que les non -2R-
        pacsAE = pacsAE.filter(removePac2R);
        logRef('! 2R', pacsAE);

        // ensuite selon le type de radiateur :

        // si plancher chauffant (heater === '6'),
        // on garde les pac Basse temperature, sinon, on les enlève.
        pacsAE = pacsAE.filter(auditSafeData.heater === '6' ? keepPacAEBasseTemp : removePacAEBasseTemp);

        // si radiateur (avec ou sans vanne) (heater === '4' || heater === '5'),
        // on garde les pac moyenne et haute temperature, sinon, on les enlève.
        pacsAE = pacsAE.filter(auditSafeData.heater === '4' || auditSafeData.heater === '5' ? keepPacAEMoyenneHauteTemp : removePacAEMoyenneHauteTemp);

        logRef("type d'emmeteur " + auditSafeData.heater + ' (' + auditSafeData.heaterName + ')', pacsAE);
    }

    // Si il ne reste rien : on return undefined :
    if (pacsAE.length <= 0) {
        logRef('terminé sans résultat ');
        return undefined;
    }

    // default approximation of energy loss
    let energyLoss = auditSafeData.surfaceHab / 10;
    try {
        const energyLossInput = api.exctractAndCheckEnergyLossInfo(audit);
        if (energyLossInput) {
            const energyLossOutput = await api.getEnergyLoss(energyLossInput);
            energyLoss = energyLossOutput.deperditions;
        }
    } catch (ex) {
        // don't do anything use our approx.
        console.log('EnergyLoss fail = ' + JSON.stringify(ex, null, 2));
    }

    // Je vais les trier par puissance décroissante:
    pacsAE.sort(compareBySplit);
    logRef('sort by split reverse (energyLoss = ' + energyLoss + ') ', pacsAE);

    // maintenant qu'on a ordonné les puissances, la première pac dont la puissance est inférieure au shon est la bonne
    for (const pack of pacsAE) {
        if (pack.mainProduct.puissance === undefined) continue; // théoriquement n'arrive pas.

        //console.log('pack ' + pack.reference + ' => ' + extractNbSplit(pack) + ' / ' + pack.mainProduct.puissance);

        // La puissance eest celle de la PAC, telle quelle. MAis a partir de 1989, on applique un facteur.
        // peut être que les pompes a chaleurs sont plus perfomantes à partir de cette date, je ne sais pas...
        let puissance = pack.mainProduct.puissance;
        if (auditSafeData.year >= 1989) puissance *= 1.05;

        // selectionner la bonne puissance selon la surface
        // on multiplie la puissance par 10 pour simplifer la comparaison.
        if (energyLoss <= puissance) {
            pack.applicable = true;
            console.log(
                '  energyLoss = ' + energyLoss.toFixed(2) + ' => ' + pack.reference + ' (' + extractNbSplit(pack) + ' / ' + pack.mainProduct.puissance + ')'
            );

            return pack;
        }
    }
    // Si on est la, on a pas trouvé.
    // ca veut dire que notre energyLoss est très petite,
    // On selectionne le dernier PAC qui a la plus petite puissance (qui sera trop grande, mais c'est pas grave)
    const lastPack = pacsAE[pacsAE.length - 1];
    lastPack.applicable = true;
    console.log('Last pack selected : ' + lastPack.reference + ' => ' + extractNbSplit(lastPack) + ' / ' + lastPack.mainProduct.puissance);

    return lastPack;
};

/**
 * Cette fonction va choisir une seule (ou zero) PAC AA, selon les données de l'audit
 * @param audit
 * @param pacsAA
 * @returns undefined si rien ne correspond. LE package PacAA non-selectionné (applicable = false) adapté à la la situation
 */
const getTheOnePacAA = (audit: any, pacsAA: Array<PackageData>): PackageData | undefined => {
    // si audit mal remplit ou si pas de packages
    if (!audit || !audit.generalOptionsMain || pacsAA.length <= 0) return undefined;

    let nbPieceAChauffer = 0;

    if (audit.generalOptionsMain && !isNaN(audit.generalOptionsMain.value)) {
        nbPieceAChauffer = +audit.generalOptionsMain.value; // QU'on se le dise, c'est le nombre de pièces.
    }
    //console.log('nombre de pièce à chauffer : ' + nbPieceAChauffer);
    // la c'est un poil compliqué :
    // je boucle sur les pack
    // je cherche celui qui a un nombre de split égal au nombre de piece a chauffer.
    // comme c'est pas sur que je trouve, je stocke le plus grand et le plus petit pour les ressortir en cas limite à la fin.

    let smallerNbSpit = 1000; // dummy value to find something smaller
    let smaller: PackageData | undefined = undefined;
    let biggerNbSplit = 0; // dummy value to find something bigger
    let bigger: PackageData | undefined = undefined;

    for (const pack of pacsAA) {
        const nbSplits = extractNbSplit(pack);
        // Si j'ai le bon nombre de splits, on prend celui là et on arrête tout.
        if (nbPieceAChauffer === nbSplits) {
            //console.log('pack ' + pack.reference + ' => ' + extractNbSplit(pack) + ' / ' + pack.mainProduct.puissance);
            return pack;
        }
        // on enregistre le plus grand et le plus petit pour le cas ou on a pas le bon résultat.
        if (nbSplits > biggerNbSplit) {
            biggerNbSplit = nbSplits;
            bigger = pack;
        }
        if (nbSplits < smallerNbSpit) {
            smallerNbSpit = nbSplits;
            smaller = pack;
        }
    }

    // Si pas trouvé, check le plus petit et le plus grand.
    // on peut affecter deux fois si le plus petit = le plus grand
    if (nbPieceAChauffer >= biggerNbSplit) {
        // console.log('pack ' + bigger?.reference + ' (' + extractNbSplit(bigger!) + ' / ' + bigger?.mainProduct.puissance + ')');
        return bigger;
    }
    if (nbPieceAChauffer <= smallerNbSpit) {
        // console.log('pack ' + smaller?.reference + ' (' + extractNbSplit(smaller!) + ' / ' + smaller?.mainProduct.puissance + ')');
        return smaller;
    }
    return undefined;
};

const extractOtherData = (
    audit: any
):
    | { isTriphase: boolean; isPec: boolean; isMultipleHydronicZone: boolean; heater: string; heaterName: string; surfaceHab: number; year: number }
    | undefined => {
    if (!audit || !audit.electricMeter || !audit.electricMeter.value_label || !audit.SHab) return undefined;
    if (!audit.heaterHotWaterIncluded || !audit.multipleHydronicZoneHeatingSystem) return undefined;
    if (!audit.heaterFeature) return undefined;
    // 3 type de comtpeur electrique : tri mono, ou inconnu.
    // dans le cas inconnu, on admet que c'est monophasé.
    const isTriphase: boolean = audit.electricMeter.value_label === 'Triphasé';
    const isPec: boolean = audit.heaterHotWaterIncluded?.value === true; // Comparer avec true (c'est important). ne pas mettre directement = audit.heaterHotWaterIncluded.value, qui sera faux, si heaterHotWaterIncluded est undefeined
    const isMultipleHydronicZone: boolean = audit.multipleHydronicZoneHeatingSystem?.value === true; //Comparer avec true (c'est important). ne pas mettre directement = audit.multipleHydronicZoneHeatingSystem.value, qui sera faux, si multipleHydronicZoneHeatingSystem est undefeined
    const heater: string = audit.heaterFeature.value;
    const heaterName: string = audit.heaterFeature.value_label;
    const surfaceHab = +audit.SHab.value;
    const year = +audit.houseAge.value;

    return { isTriphase, isPec, isMultipleHydronicZone, heater, heaterName, surfaceHab, year };
};
